import React, { useState, useEffect } from 'react';
import { Box, Container, Grid } from '@mui/material';
import Sidebar from './components/Sidebar';
import NoteList from './components/NoteList';
import NoteModal from './components/NoteModal';
import CategoryModal from './components/CategoryModal';
import { v4 as uuidv4 } from 'uuid';

function App() {
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('categories');
    return saved ? JSON.parse(saved) : [];
  });

  const [notes, setNotes] = useState(() => {
    const saved = localStorage.getItem('notes');
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    localStorage.setItem('categories', JSON.stringify(categories));
    localStorage.setItem('notes', JSON.stringify(notes));
  }, [categories, notes]);

  const handleAddCategory = (category) => {
    if (editingCategory) {
      setCategories(
        categories.map((cat) =>
          cat.id === editingCategory.id
            ? { ...category, id: editingCategory.id }
            : cat
        )
      );
      setEditingCategory(null);
    } else {
      const newCategory = { ...category, id: uuidv4() };
      setCategories([...categories, newCategory]);
    }
    setCategoryModalOpen(false);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryModalOpen(true);
  };

  const handleDeleteCategory = (categoryId) => {
    setCategories(categories.filter((cat) => cat.id !== categoryId));
    setNotes(notes.filter((note) => note.categoryId !== categoryId));
  };

  const handleAddNote = (note) => {
    if (editingNote) {
      setNotes(
        notes.map((n) =>
          n.id === editingNote.id
            ? {
                ...note,
                id: editingNote.id,
                timestamp: editingNote.timestamp,
                lastEdited: new Date().toISOString(),
              }
            : n
        )
      );
      setEditingNote(null);
    } else {
      setNotes([
        ...notes,
        {
          ...note,
          id: uuidv4(),
          timestamp: new Date().toISOString(),
        },
      ]);
    }
    setNoteModalOpen(false);
  };

  const handleDeleteNote = (noteId) => {
    setNotes(notes.filter((note) => note.id !== noteId));
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteModalOpen(true);
  };

  const filteredNotes = selectedCategory
    ? notes.filter((note) => note.categoryId === selectedCategory)
    : notes;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        categories={categories}
        onAddCategory={() => {
          setEditingCategory(null);
          setCategoryModalOpen(true);
        }}
        onSelectCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        onDeleteCategory={handleDeleteCategory}
        onEditCategory={handleEditCategory}
      />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <NoteList
              notes={filteredNotes}
              categories={categories}
              onAddNote={() => {
                setEditingNote(null);
                setNoteModalOpen(true);
              }}
              onEditNote={handleEditNote}
              onDeleteNote={handleDeleteNote}
            />
          </Grid>
        </Container>
      </Box>

      <NoteModal
        open={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
          setEditingNote(null);
        }}
        onSave={handleAddNote}
        categories={categories}
        note={editingNote}
      />

      <CategoryModal
        open={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setEditingCategory(null);
        }}
        onSave={handleAddCategory}
        category={editingCategory}
      />
    </Box>
  );
}

export default App;
